import { createRootRoute, Outlet, useNavigate, useRouterState } from '@tanstack/react-router';
import { useSnapshot } from 'valtio';
import { useEffect } from 'react';
import { Header } from '../components/layout/Header';
import { IdleLogoutGuard } from '../components/layout/IdleLogoutGuard';
import { authState } from '../stores/authStore';

/** Routes that don't require authentication. */
const PUBLIC_ROUTES = ['/login', '/register', '/forgot-password', '/reset-password', '/accept-invite'];

function isPublicRoute(pathname: string): boolean {
  return PUBLIC_ROUTES.some((r) => pathname === r || pathname.startsWith(r + '/'));
}

function RootComponent() {
  const { user, isInitialised } = useSnapshot(authState);
  const navigate = useNavigate();
  const routerState = useRouterState();
  const pathname = routerState.location.pathname;

  useEffect(() => {
    if (!isInitialised) return;

    if (!user && !isPublicRoute(pathname)) {
      // Not authenticated and trying to access a protected route → redirect to login
      navigate({ to: '/login' });
    } else if (user && isPublicRoute(pathname)) {
      // Authenticated but on a public route → redirect to dashboard
      navigate({ to: '/' });
    }
  }, [user, isInitialised, pathname, navigate]);

  // While auth is initialising, show a minimal loading indicator
  if (!isInitialised) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block w-8 h-8 border-4 border-purple-200 border-t-purple-600 rounded-full animate-spin" />
          <p className="text-gray-500 mt-4 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Public routes get no Header
  if (isPublicRoute(pathname)) {
    return <Outlet />;
  }

  // Protected routes get the standard layout
  return (
    <div className="flex flex-col h-full overflow-hidden">
      <IdleLogoutGuard />
      <Header />
      <div className="flex-1 min-h-0 flex flex-col">
        <Outlet />
      </div>
    </div>
  );
}

export const Route = createRootRoute({
  component: RootComponent,
});
